// src/lib/firebase.ts
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore, serverTimestamp as firestoreServerTimestamp } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID, // Optional
  googleClientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
};

// Check for essential Firebase configuration keys
const essentialConfigKeys: (keyof typeof firebaseConfig)[] = [
  'apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'
];

const missingOrEmptyEssentialKeys = essentialConfigKeys.filter(
  key => !firebaseConfig[key as keyof typeof firebaseConfig] || String(firebaseConfig[key as keyof typeof firebaseConfig]).trim() === ''
);

if (missingOrEmptyEssentialKeys.length > 0) {
  const errorDetails = missingOrEmptyEssentialKeys
    .map(key => `NEXT_PUBLIC_FIREBASE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`)
    .join(', ');

  const errorMessage = 
    `!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!\n` +
    `FIREBASE STARTUP FAILED:\n` +
    `Essential Firebase config variables are missing or empty: ${errorDetails}.\n\n` +
    `To enable Firebase, ensure all required NEXT_PUBLIC_ variables are set in your '.env.local' file and restart the server.\n` +
    `!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!`;
  
  // This will crash the build/server start if config is missing, which is what we want for a hard failure.
  throw new Error(errorMessage);
}

const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

let auth: Auth;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
const serverTimestamp = firestoreServerTimestamp;

try {
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
} catch (e) {
  console.error("Error initializing Firebase services. This can happen in environments where Firebase is blocked or not configured correctly.", e);
  // Set services to null to allow the app to run in a degraded state
  // and show appropriate error messages in the UI.
  auth = getAuth(); // getAuth() can be called without app, but will fail on use
  db = null;
  storage = null;
}

export { app, auth, db, storage, serverTimestamp, firebaseConfig };
